/**
 * RecipientInfo Component
 *
 * Displays recipient name, email, channel badges, and alert level toggle.
 * Central information display area of the recipient card.
 *
 * @module RecipientItem/RecipientInfo
 */

import { BlockStack, Icon, InlineStack, Text } from '@shopify/polaris';
import { EmailIcon } from '@shopify/polaris-icons';

import type { AlertLevel, ChannelStatus } from './types';
import { ChannelBadge } from './ChannelBadge';
import { AlertBadgeToggle } from './AlertBadgeToggle';

export interface RecipientInfoProps {
  /** Recipient name */
  name: string;
  /** Recipient email address */
  email: string;
  /** Whether the recipient is verified */
  isVerified: boolean;
  /** Alert levels assigned to this recipient */
  alertLevels: AlertLevel[];
  /** Whether alert badges are expanded */
  alertBadgesExpanded: boolean;
  /** Whether to show the pop-in animation on alert badges */
  showAlertBadgesPopAnimation?: boolean;
  /** Callback when alert badges toggle is clicked */
  onToggleAlertBadges: () => void;
  /** Channel statuses */
  channels: {
    digest: ChannelStatus;
    instant: ChannelStatus;
  };
  /** Whether digest toggle is loading */
  isTogglingDigest: boolean;
  /** Whether instant toggle is loading */
  isTogglingInstant: boolean;
  /** Callback when digest channel is toggled */
  onToggleDigest: () => void;
  /** Callback when instant channel is toggled */
  onToggleInstant: () => void;
}

/**
 * Recipient information display component
 *
 * Shows the recipient's name, email, channel badges, and collapsible alert badges.
 * Opacity is reduced when recipient is unverified.
 */
export function RecipientInfo({
  name,
  email,
  isVerified,
  alertLevels,
  alertBadgesExpanded,
  showAlertBadgesPopAnimation = false,
  onToggleAlertBadges,
  channels,
  isTogglingDigest,
  isTogglingInstant,
  onToggleDigest,
  onToggleInstant,
}: RecipientInfoProps) {
  // Build class names for verified/unverified state
  const containerClasses = [
    'recipient-info-container',
    !isVerified && 'recipient-info-container-unverified',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={containerClasses}>
      <BlockStack>
        <Text as="p" variant="bodyMd" fontWeight="semibold">
          {name}
        </Text>
        <InlineStack gap="200" blockAlign="center" wrap={false}>
          {/* Email with icon */}
          <InlineStack gap="100" blockAlign="center" wrap={false}>
            <div className="recipient-email-icon">
              <Icon source={EmailIcon} tone="subdued" />
            </div>
            <Text as="p" variant="bodySm" tone="subdued" truncate>
              {email}
            </Text>
          </InlineStack>

          {/* Channel badges - only for verified recipients */}
          {isVerified && (
            <InlineStack gap="100" blockAlign="center" wrap={false}>
              <ChannelBadge
                type="digest"
                status={channels.digest}
                isLoading={isTogglingDigest}
                onToggle={onToggleDigest}
              />
              <ChannelBadge
                type="instant"
                status={channels.instant}
                isLoading={isTogglingInstant}
                onToggle={onToggleInstant}
              />
            </InlineStack>
          )}

          {/* Collapsible alert level badges - only for verified recipients with levels */}
          {isVerified && alertLevels.length > 0 && (
            <AlertBadgeToggle
              alertLevels={alertLevels}
              isExpanded={alertBadgesExpanded}
              showPopAnimation={showAlertBadgesPopAnimation}
              onToggle={onToggleAlertBadges}
            />
          )}
        </InlineStack>
      </BlockStack>
    </div>
  );
}
